import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import _ from "lodash";
import * as yup from "yup";

import { FormQuestionConfiguration } from "@whoosh/design-system/src/components/controls/EventFormRenderer";
import { decodeCheckboxSelection } from "@whoosh/design-system/src/components/controls/EventFormRenderer/checkboxSelection";
import { EventChargePayerRowData } from "@whoosh/design-system/src/components/display/ChargePayerRow/ChargePayerRow";

import { PerGroupSelectionData } from "../components/PrimaryChargeSelectionStep/components/PerGroupSelection";
import { RegistrationChargeRowPerson } from "../components/PrimaryChargeSelectionStep/components/RegistrationChargeSelectionRow";
import { EventCapacityType } from "../components/__generated__/FormStepManager_event.graphql";
import {
  EventFormResponseInput,
  EventPlayer,
  EventSignUpProcessSteps,
  EventSignUpRegistration,
  EventSignUpRow,
  PrimaryChargeQuickPayOption,
  UnknownGuestPrimaryChargePaymentMap,
} from "../types";
import { getEventPrimaryChargePaymentData } from "../utils";
import { getChargeEligibilityClientId } from "../utils/getChargeEligibilityClientId";
import {
  hydrateUnknownGuestPlayerCountsAtom,
  zeroOutUnknownGuestPaymentsForPayer,
} from "./actions";
import {
  eventRemainingCapacityAtom,
  unknownGuestsAssignedToPayersAtom,
} from "./stores";

/**
 * Step Info
 */
export const requiredEventSignUpStepsAtom = atom<EventSignUpProcessSteps[]>([]);
export const currentEventSignUpStepAtom = atom<EventSignUpProcessSteps | null>(
  null
);
const resolvedPrimaryChargeSelectionQueryKeysAtom = atom<string[]>([]);

const primaryChargeSelectionQueryKey = (
  index: number | null,
  clientId: string
) => `${index ?? "unknown"}:${clientId}`;

export const currentStepIndexAtom = atom<number | null>((get) => {
  const requiredSteps = get(requiredEventSignUpStepsAtom);
  const currentStep = get(currentEventSignUpStepAtom);
  if (!currentStep) return null;
  return requiredSteps.indexOf(currentStep);
});
export const isFirstStepAtom = atom((get) => {
  const currentStepIndex = get(currentStepIndexAtom);
  return currentStepIndex === 0;
});
export const isLastStepAtom = atom((get) => {
  const currentStepIndex = get(currentStepIndexAtom);
  const requiredSteps = get(requiredEventSignUpStepsAtom);
  return currentStepIndex === requiredSteps.length - 1;
});
export const isCurrentStepValidAtom = atom((get) => {
  const currentStep = get(currentEventSignUpStepAtom);
  switch (currentStep) {
    case "PEOPLE":
      return get(isCurrentGroupValidAtom);
    case "FORM":
      return get(isCurrentEventFormValidAtom);
    case "PRIMARY_CHARGE_SELECTION":
      return get(isPrimaryChargeSelectionStepValidAtom);
    default:
      return true;
  }
});

const _currentMemberUserIdAtom = atom<string | null>(null);
export const currentMemberUserIdAtom = atom((get) =>
  get(_currentMemberUserIdAtom)
);

// Whether the staff user driving the form is a department admin. Used to gate
// ADMIN_ONLY charges in the staff sign-up flow. Always false for member users.
const _isStaffAdminAtom = atom<boolean>(false);
export const isStaffAdminAtom = atom((get) => get(_isStaffAdminAtom));

export const userTypeAtom = atom<"STAFF" | "MEMBER">((get) => {
  const currentMemberUserId = get(_currentMemberUserIdAtom);
  return currentMemberUserId ? "MEMBER" : "STAFF";
});

export const isAddMemberStepOnlyAtom = atom((get) => {
  const requiredSteps = get(requiredEventSignUpStepsAtom);
  return requiredSteps.length === 1 && requiredSteps[0] === "PEOPLE";
});

const _originalSignUpRowAtom = atom<EventSignUpRow | null>(null);
const _currentSignUpRowAtom = atom<EventSignUpRow | null>(null);

export const originalSignUpRowAtom = atom(
  (get) => get(_originalSignUpRowAtom),
  (get, set, payload: EventSignUpRow | null) => {
    set(_originalSignUpRowAtom, payload);
  }
);
export const currentSignUpRowAtom = atom(
  (get) => get(_currentSignUpRowAtom),
  (get, set, payload: EventSignUpRow | null) => {
    set(_currentSignUpRowAtom, payload);
  }
);

// This should not default to false, we can assume spotsAvailable on event is null, then they are unlimited.
export const isGroupFullOfAttendeesAtom = atomFamily(
  (spotsAvailable: number | null) =>
    atom<boolean>((get) => {
      const isStaff = get(isStaffAtom);
      const isNewSignUp = get(_isNewEventSignUpAtom);
      const eventHasWaitlist = get(_eventHasWaitlistAtom);
      const signUpRow = get(currentSignUpRowAtom);
      const originalSignUpRow = get(originalSignUpRowAtom);
      const originalRegistrationsCount = originalSignUpRow?.registrations
        ? originalSignUpRow?.registrations?.length
        : 0;
      const registrations =
        signUpRow?.registrations.map((r, i) => {
          return { ...r, index: i };
        }) ?? [];
      let isGroupFull = false;

      if (isNewSignUp) {
        isGroupFull =
          !eventHasWaitlist && spotsAvailable === registrations.length;
      } else if (spotsAvailable) {
        const groupSize = originalRegistrationsCount + spotsAvailable;
        isGroupFull = groupSize === registrations.length;
      }

      const cannotGoOverLimit = isGroupFull && !isStaff;

      return cannotGoOverLimit;
    })
);

const _isWaitlistSignupAtom = atom(false);

export const isCurrentSignUpOnWaitlistAtom = atom((get) => {
  const signUpRow = get(currentSignUpRowAtom);
  if (
    signUpRow?.waitlistIndex !== null &&
    signUpRow?.waitlistIndex !== undefined
  ) {
    return true;
  }

  if (!get(_isNewEventSignUpAtom)) return false;
  if (get(_isWaitlistSignupAtom)) return true;

  const eventRemainingCapacity = get(eventRemainingCapacityAtom);
  const registrationCount = signUpRow?.registrations.length ?? 0;
  const isBeyondEventCapacity =
    (eventRemainingCapacity?.capacityType === "ATTENDEES" &&
      registrationCount > eventRemainingCapacity.remainingCapacity) ||
    (eventRemainingCapacity?.capacityType === "GROUPS" &&
      eventRemainingCapacity.remainingCapacity <= 0);

  return get(_eventHasWaitlistAtom) && isBeyondEventCapacity;
});

type PrimaryChargeType = "PER_PERSON" | "PER_SIGN_UP";

export const defaultPrimaryChargeRuleIdAtom = atom<string | null>(null);
const _primaryChargeTypeAtom = atom<PrimaryChargeType | null>(null);
export const primaryChargeRuleIdtoPriceMapAtom = atom<
  { id: string; price: number }[]
>([]);

export const mergePrimaryChargeRulePricesAtom = atom(
  null,
  (
    get,
    set,
    prices: {
      id: string;
      price: number;
    }[]
  ) => {
    const currentPrices = get(primaryChargeRuleIdtoPriceMapAtom);
    const mergedPrices = [...currentPrices];

    prices.forEach((price) => {
      const existingPriceIndex = mergedPrices.findIndex(
        (existingPrice) => existingPrice.id === price.id
      );

      if (existingPriceIndex === -1) {
        mergedPrices.push(price);
      } else {
        mergedPrices[existingPriceIndex] = price;
      }
    });

    if (!_.isEqual(currentPrices, mergedPrices)) {
      set(primaryChargeRuleIdtoPriceMapAtom, mergedPrices);
    }
  }
);

const _groupSizeRequirementsAtom = atom<{
  minMemberCount: number;
  minGuestCount: number;
  maxMemberCount: number;
  maxGuestCount: number;
  allowedGroupSizes: (number | null | undefined)[];
} | null>(null);

export const _isNewEventSignUpAtom = atom<boolean>(true);

export const isStaffAtom = atom<boolean>(false);

export const isGroupEventAtom = atom((get) => {
  const groupSizeRequirements = get(_groupSizeRequirementsAtom);
  return groupSizeRequirements
    ? groupSizeRequirements.maxMemberCount +
        groupSizeRequirements.maxGuestCount >
        1
    : false;
});

export const _isMemberSignUpProxyAllowedAtom = atom<boolean>(false);
export const _eventHasWaitlistAtom = atom<boolean>(false);

export const memberRegistrationsAtom = atom<EventSignUpRegistration[]>(
  (get) => {
    const signUpRow = get(currentSignUpRowAtom);
    return signUpRow?.registrations.filter((r) => r.type === "MEMBER") ?? [];
  }
);

export const memberHoldRegistrationsAtom = atom<EventSignUpRegistration[]>(
  (get) => {
    const signUpRow = get(currentSignUpRowAtom);
    return (
      signUpRow?.registrations.filter(
        (r) => r.type === "MEMBER" && !r.registrant.playerId
      ) ?? []
    );
  }
);

export const unknownGuestRegistrationsAtom = atom<EventSignUpRegistration[]>(
  (get) => {
    const signUpRow = get(currentSignUpRowAtom);
    return (
      signUpRow?.registrations.filter(
        (r) => r.type === "GUEST" && !r.registrant.playerId
      ) ?? []
    );
  }
);

export const guestRegistrationsAtom = atom<EventSignUpRegistration[]>((get) => {
  const signUpRow = get(currentSignUpRowAtom);
  return (
    signUpRow?.registrations.filter(
      (r) => r.type === "GUEST" || r.type === "New Guest"
    ) ?? []
  );
});

export const customerRegistrationsAtom = atom<EventSignUpRegistration[]>(
  (get) => {
    const signUpRow = get(currentSignUpRowAtom);
    return signUpRow?.registrations.filter((r) => r.type === "CUSTOMER") ?? [];
  }
);

const _memberRegistrationsCountAtom = atom<number>(
  (get) => get(memberRegistrationsAtom).length
);

const _guestRegistrationsCountAtom = atom<number>(
  (get) => get(guestRegistrationsAtom).length
);

const _customerRegistrationsCountAtom = atom<number>(
  (get) => get(customerRegistrationsAtom).length
);

export const allowedGroupSizesAtom = atom<(number | null)[]>((get) => {
  const groupSizeRequirements = get(_groupSizeRequirementsAtom);
  const allowedGroupSizes =
    groupSizeRequirements?.allowedGroupSizes.flatMap((ags) =>
      ags ? ags : []
    ) || [];

  return allowedGroupSizes;
});

export const isInAllowedGroupSizesAtom = atom<boolean>((get) => {
  const groupSizeRequirements = get(_groupSizeRequirementsAtom);

  const numMemberRegistrations = get(_memberRegistrationsCountAtom);
  const numGuestRegistrations = get(_guestRegistrationsCountAtom);
  const numCustomerRegistrations = get(_customerRegistrationsCountAtom);

  const numRegistrations =
    (numMemberRegistrations || 0) +
    (numGuestRegistrations || 0) +
    (numCustomerRegistrations || 0);

  const allowedGroupSizes = groupSizeRequirements?.allowedGroupSizes || [];

  return allowedGroupSizes.includes(numRegistrations);
});

export const remainingEventCapacityAtom = atom<number | null>(null);

// final check, maxGroupSize has to be one of the allowedGroupSizesFiltered
const findMaxValidGroupSize = (
  allowedGroupSizesFiltered: number[],
  maxGroupSize: number
): number => {
  allowedGroupSizesFiltered.sort((a, b) => a - b);

  let closestSize = allowedGroupSizesFiltered[0];

  for (const size of allowedGroupSizesFiltered) {
    if (size === maxGroupSize) {
      return size;
    } else if (size < maxGroupSize) {
      closestSize = size;
    } else {
      break;
    }
  }

  return closestSize;
};

// need to modify to take into account the original
export const isGroupFullOfMembersAtom = atom<boolean>((get) => {
  const registrationsCount =
    get(currentSignUpRowAtom)?.registrations?.length || 0;

  const originalRegistrationsCount =
    get(originalSignUpRowAtom)?.registrations?.length || 0;

  const remainingEventCapacityStored = get(remainingEventCapacityAtom) ?? 0;
  const remainingEventCapacity =
    remainingEventCapacityStored > 0 ? remainingEventCapacityStored : 0;

  const isNewSignUp = get(_isNewEventSignUpAtom);

  const isStaff = get(isStaffAtom);

  const alternateMaxGroupSize =
    isNewSignUp || isStaff
      ? 1000
      : remainingEventCapacity + originalRegistrationsCount;

  const groupSizeRequirements = get(_groupSizeRequirementsAtom);

  const maxMemberCount = groupSizeRequirements?.maxMemberCount ?? 1;
  const numMemberRegistrations = get(memberRegistrationsAtom).length ?? 0;

  const numEmptyMemberSlots =
    maxMemberCount > numMemberRegistrations
      ? maxMemberCount - numMemberRegistrations
      : 0;

  const allowedGroupSizesFiltered =
    groupSizeRequirements?.allowedGroupSizes.map((size) => size || 0) ?? [];

  const maxGroupSize = findMaxValidGroupSize(
    allowedGroupSizesFiltered,
    alternateMaxGroupSize
  );

  const isGroupFull = maxGroupSize <= registrationsCount;
  const isMemberGroupFull = numEmptyMemberSlots === 0;

  return isGroupFull || isMemberGroupFull;
});

export const isGroupFulOfGuestsAtom = atom<boolean>((get) => {
  const registrationsCount =
    get(currentSignUpRowAtom)?.registrations?.length || 0;

  const originalRegistrationsCount =
    get(originalSignUpRowAtom)?.registrations?.length || 0;

  const remainingEventCapacityStored = get(remainingEventCapacityAtom) ?? 0;
  const remainingEventCapacity =
    remainingEventCapacityStored > 0 ? remainingEventCapacityStored : 0;
  const isNewSignUp = get(_isNewEventSignUpAtom);
  const isStaff = get(isStaffAtom);

  const alternateMaxGroupSize =
    isNewSignUp || isStaff
      ? 1000
      : remainingEventCapacity + originalRegistrationsCount;

  const groupSizeRequirements = get(_groupSizeRequirementsAtom);

  const maxGuestCount = groupSizeRequirements?.maxGuestCount ?? 1;
  const numGuestRegistrations = get(guestRegistrationsAtom).length ?? 0;

  const numEmptyGuestSlots =
    maxGuestCount > numGuestRegistrations
      ? maxGuestCount - numGuestRegistrations
      : 0;

  const allowedGroupSizesFiltered =
    groupSizeRequirements?.allowedGroupSizes.map((size) => size || 0) ?? [];

  const maxGroupSize = findMaxValidGroupSize(
    allowedGroupSizesFiltered,
    alternateMaxGroupSize
  );
  const isGroupFull = maxGroupSize <= registrationsCount;
  const isGuestGroupFull = numEmptyGuestSlots === 0;

  return isGroupFull || isGuestGroupFull;
});

export const isGroupFullOfCustomersAtom = atom<boolean>((get) => {
  const registrationsCount =
    get(currentSignUpRowAtom)?.registrations?.length || 0;

  const originalRegistrationsCount =
    get(originalSignUpRowAtom)?.registrations?.length || 0;

  const remainingEventCapacityStored = get(remainingEventCapacityAtom) ?? 0;
  const remainingEventCapacity =
    remainingEventCapacityStored > 0 ? remainingEventCapacityStored : 0;

  const isNewSignUp = get(_isNewEventSignUpAtom);
  const isStaff = get(isStaffAtom);

  const alternateMaxGroupSize =
    isNewSignUp || isStaff
      ? 1000
      : remainingEventCapacity + originalRegistrationsCount;

  const groupSizeRequirements = get(_groupSizeRequirementsAtom);

  const maxCustomerCount = groupSizeRequirements?.maxMemberCount ?? 1;
  const numCustomerRegistrations = get(customerRegistrationsAtom).length ?? 0;

  const numEmptyCustomerSlots =
    maxCustomerCount > numCustomerRegistrations
      ? maxCustomerCount - numCustomerRegistrations
      : 0;

  const allowedGroupSizesFiltered =
    groupSizeRequirements?.allowedGroupSizes.map((size) => size || 0) ?? [];

  const maxGroupSize = findMaxValidGroupSize(
    allowedGroupSizesFiltered,
    alternateMaxGroupSize
  );

  const isGroupFull = maxGroupSize <= registrationsCount;
  const isCustomerGroupFull = numEmptyCustomerSlots === 0;

  return isGroupFull || isCustomerGroupFull;
});

export const isCurrentGroupValidAtom = atom<boolean>((get) => {
  const groupSizeRequirements = get(_groupSizeRequirementsAtom);
  const isInAllowedGroupSizes = get(isInAllowedGroupSizesAtom);

  const memberRegistrations = get(memberRegistrationsAtom);

  const memberHoldRegistrations = get(memberHoldRegistrationsAtom);

  const guestRegistrations = get(guestRegistrationsAtom);

  const customerRegistrations = get(customerRegistrationsAtom);

  const isPublic = customerRegistrations.length > 0;

  if (isPublic) {
    const isCustomerCountValid = groupSizeRequirements
      ? customerRegistrations.length >= groupSizeRequirements.minMemberCount &&
        customerRegistrations.length <= groupSizeRequirements.maxMemberCount
      : false;

    return isCustomerCountValid && isInAllowedGroupSizes;
  } else {
    const isGuestCountValid = groupSizeRequirements
      ? guestRegistrations.length >= groupSizeRequirements.minGuestCount &&
        guestRegistrations.length <= groupSizeRequirements.maxGuestCount
      : false;

    const isMemberCountValid = groupSizeRequirements
      ? memberRegistrations.length - memberHoldRegistrations.length >=
          groupSizeRequirements.minMemberCount &&
        memberRegistrations.length <= groupSizeRequirements.maxMemberCount
      : false;

    return groupSizeRequirements
      ? isMemberCountValid && isGuestCountValid && isInAllowedGroupSizes
      : false;
  }
});

export const isCurrentMemberOwnerAtom = atom((get) => {
  const currentMemberUserId = get(currentMemberUserIdAtom);
  const currentSignUpRow = get(_currentSignUpRowAtom);

  if (!currentMemberUserId || !currentSignUpRow) return false;

  return currentMemberUserId === currentSignUpRow.ownerClient?.id;
});

export const isCurrentMemberRegisteredAtom = atom((get) => {
  const currentMemberUserId = get(currentMemberUserIdAtom);
  const currentSignUpRow = get(_currentSignUpRowAtom);

  if (!currentMemberUserId || !currentSignUpRow) return false;

  return currentSignUpRow.registrations.some(
    (r) => r.registrant.id === currentMemberUserId
  );
});

/**
 * Form Related Atoms
 */
const _eventDetailFormConfigurationAtom = atom<
  FormQuestionConfiguration[] | null
>(null);
export const eventDetailFormConfigurationAtom = atom((get) =>
  get(_eventDetailFormConfigurationAtom)
);

export const isCurrentEventFormDirtyAtom = atom((get) => {
  const _originalSignUpRow = get(_originalSignUpRowAtom);
  const _currentSignUpRow = get(_currentSignUpRowAtom);

  if (!_originalSignUpRow || !_currentSignUpRow) return false;

  // Compare form responses between original and current registrations
  const isDirty = _originalSignUpRow.registrations.some(
    (originalReg, index) => {
      const currentReg = _currentSignUpRow.registrations[index];
      if (!currentReg) return true;

      // If original has no formResponses but current has empty/undefined responses, consider not dirty
      if (!originalReg.formResponses && currentReg.formResponses) {
        const allEmpty = currentReg.formResponses.every(
          (response) => !response.stringValue || response.stringValue === ""
        );
        if (allEmpty) return false;
      }

      // Deep compare the formResponses arrays
      return !_.isEqual(originalReg.formResponses, currentReg.formResponses);
    }
  );

  return isDirty;
});

export const isCurrentEventFormValidAtom = atom((get) => {
  const questionConfiguration = get(eventDetailFormConfigurationAtom);
  const currentSignUpRow = get(currentSignUpRowAtom);

  /**
   * We always need to validate email
   */
  const questionsToValidate = questionConfiguration?.filter(
    (q) =>
      !!q.enabled &&
      (q.type === "EMAIL" ||
        q.guestVisibility === "REQUIRED" ||
        q.memberVisibility === "REQUIRED")
  );

  const isValid = !!questionsToValidate?.every((q) => {
    return currentSignUpRow?.registrations
      .filter((r) => !!r.registrant.playerId || r.type === "CUSTOMER") // do not validate for member holds or unknown guests
      .every((r) => {
        const isKnownPatron =
          r.type === "MEMBER" ||
          (r.type === "CUSTOMER" && !!r.registrant.playerId);

        const isRequired = isKnownPatron
          ? q.memberVisibility === "REQUIRED"
          : q.guestVisibility === "REQUIRED";

        /**
         * Currently only Email field types have validation outside of 'required'
         */
        if (!isRequired && q.type !== "EMAIL") return true;

        const formResponse = r.formResponses?.find(
          (f) => f.questionConfigurationId === q.id
        );

        const { stringValue, booleanValue, transportationTypeId } =
          formResponse ?? {};

        switch (q.type) {
          case "BOOLEAN":
            return booleanValue !== undefined;
          case "TOGGLE":
            return booleanValue !== undefined;
          case "TEXT":
            return !!stringValue;
          case "LONG_TEXT":
            return !!stringValue;
          case "EMAIL":
            //If it isn't required, and there is no value, don't validate
            if (!isRequired && !stringValue) return true;
            const schema = yup.string().email();
            let isEmail = false;
            try {
              isEmail = !!schema.validateSync(stringValue);
            } catch {
              isEmail = false;
            }
            return !!stringValue && isEmail;
          case "GHIN_NUMBER":
            return !!stringValue;
          case "TRANSPORTATION":
            if (!!transportationTypeId) {
            }
            return !!transportationTypeId;
          case "MULTIPLE_CHOICE":
            return !!stringValue;
          case "CHECKBOXES":
            return !!decodeCheckboxSelection(stringValue, q.values)?.length;
          default:
            return !!stringValue;
        }
      });
  });

  return isValid;
});

const onSaveCallbackAtom = atom<((row: EventSignUpRow) => void) | null>(null);

/**
 * Primary Charge Info
 */
//Select Primary Charges

//Select Per Person Data
export const primaryChargeSelectionRegistrantListAtom = atom((get) => {
  const currentRow = get(currentSignUpRowAtom);
  const originalRow = get(originalSignUpRowAtom);
  const currentUserMemberId = get(currentMemberUserIdAtom);
  const registrants: RegistrationChargeRowPerson[] =
    currentRow?.registrations.map((r) => {
      const original = originalRow?.registrations.find(
        (registration) => !!r.id && registration.id === r.id
      );
      const canPreserveAssignedCharge =
        original &&
        original.type === r.type &&
        original.registrant.playerId === r.registrant.playerId &&
        original.payingMemberId === r.payingMemberId &&
        (r.type !== "GUEST" ||
          !!r.payingMemberId ||
          originalRow?.ownerClient?.id === currentRow?.ownerClient?.id);
      return {
        firstName: r.registrant.firstName ?? null,
        lastName: r.registrant.lastName ?? null,
        id: r.registrant.playerId ?? "",
        chargeEligibilityClientId: getChargeEligibilityClientId(r),
        registrationId: canPreserveAssignedCharge ? r.id : null,
        profileImageId: r.registrant.profileImage?.publicId,
        detail: r.registrant.detail,
        selectedOption: r.primaryRegistrationChargeTypeId,
        index: r.index,
        type: r.type === "MEMBER" ? "member" : "guest",
        isCurrentUser: r.registrant.playerId === currentUserMemberId,
      };
    }) ?? [];

  return registrants;
});

export const arePrimaryChargeSelectionQueriesResolvedAtom = atom((get) => {
  const resolvedQueryKeys = get(resolvedPrimaryChargeSelectionQueryKeysAtom);

  return get(primaryChargeSelectionRegistrantListAtom).every((registrant) => {
    if (!registrant.chargeEligibilityClientId) return true;

    return resolvedQueryKeys.includes(
      primaryChargeSelectionQueryKey(
        registrant.index,
        registrant.chargeEligibilityClientId
      )
    );
  });
});

export const markPrimaryChargeSelectionQueryResolvedAtom = atom(
  null,
  (
    get,
    set,
    payload: { index: number | null; chargeEligibilityClientId: string }
  ) => {
    const resolvedQueryKeys = get(resolvedPrimaryChargeSelectionQueryKeysAtom);
    const queryKey = primaryChargeSelectionQueryKey(
      payload.index,
      payload.chargeEligibilityClientId
    );

    if (!resolvedQueryKeys.includes(queryKey)) {
      set(resolvedPrimaryChargeSelectionQueryKeysAtom, [
        ...resolvedQueryKeys,
        queryKey,
      ]);
    }
  }
);

//Select by whole group data
export const primaryChargeSelectionByGroupData = atom((get) => {
  const currentRow = get(currentSignUpRowAtom);
  const defaultPrimaryChargeId = get(defaultPrimaryChargeRuleIdAtom);
  const currentSelectedPrimaryChargeId =
    currentRow?.registrations.find((r) => r.primaryRegistrationChargeTypeId)
      ?.primaryRegistrationChargeTypeId ?? defaultPrimaryChargeId;
  const avatarList = currentRow?.registrations.flatMap((r) => {
    return {
      profileImageId: r.registrant.profileImage?.publicId,
      firstName: r.registrant.firstName ?? null,
      lastName: r.registrant.lastName ?? null,
      type: r.type === "MEMBER" ? "member" : ("guest" as "member" | "guest"),
    };
  });
  const data: PerGroupSelectionData = {
    avatarList,
    currentSelectedPrimaryChargeId,
  };
  return data;
});

export const isPrimaryChargeSelectionStepValidAtom = atom((get) => {
  const primaryChargeType = get(_primaryChargeTypeAtom);

  if (primaryChargeType === "PER_SIGN_UP") {
    const currentSelectedPrimaryChargeId = get(
      primaryChargeSelectionByGroupData
    ).currentSelectedPrimaryChargeId;

    return !!currentSelectedPrimaryChargeId;
  }

  return get(arePrimaryChargeSelectionQueriesResolvedAtom);
});

const _primaryChargeQuickPayOptionAtom = atom<PrimaryChargeQuickPayOption>(
  "you_pay_for_everyone"
);

export const primaryChargeQuickPayOptionAtom = atom((get) => {
  const selectedQuickPayOption = get(_primaryChargeQuickPayOptionAtom);

  const currentSignUpRow = get(currentSignUpRowAtom);

  const firstSignupMemberId =
    currentSignUpRow?.registrations[0]?.registrant?.playerId;

  const firstMemberIsPayingForEverySignUp =
    currentSignUpRow?.registrations?.every(
      (r) => r.payingMemberId === firstSignupMemberId
    );

  const isOwnerPayingForEverySignUp = currentSignUpRow?.registrations?.every(
    (r) => r.payingMemberId === currentSignUpRow?.ownerClient?.id
  );

  return selectedQuickPayOption === "custom"
    ? "custom"
    : selectedQuickPayOption === "everyone_pays_for_themselves"
    ? "everyone_pays_for_themselves"
    : firstMemberIsPayingForEverySignUp || isOwnerPayingForEverySignUp
    ? "you_pay_for_everyone"
    : "custom";
});

export const eventPrimaryChargePaymentDataAtom = atom<
  EventChargePayerRowData[]
>((get) => {
  const currentUserMemberId = get(currentMemberUserIdAtom);
  const currentSignUpRow = get(currentSignUpRowAtom);
  const primaryChargeIdToPriceMap = get(primaryChargeRuleIdtoPriceMapAtom);
  const memberSignUpProxyAllowed = get(_isMemberSignUpProxyAllowedAtom);

  const chargeData = getEventPrimaryChargePaymentData({
    currentSignUpRow,
    primaryChargeIdToPriceMap,
    currentUserMemberId,
    memberSignUpProxyAllowed,
  });

  if (!currentSignUpRow) return [];

  return chargeData;
});

/**
 * ACTIONS
 */

/**
 * Called once at the start to set up all of the data we need for the whole
 * signup form.
 */
export const initializeEventSignUpProcessAtom = atom(
  null,
  (
    get,
    set,
    payload: {
      steps: EventSignUpProcessSteps[];
      signUpRow: EventSignUpRow;
      currentMemberUserId: string | null;
      formQuestionConfigurations: FormQuestionConfiguration[];
      groupSizeRequirements: {
        minMemberCount: number;
        minGuestCount: number;
        maxMemberCount: number;
        maxGuestCount: number;
        allowedGroupSizes: (number | null | undefined)[];
      };
      defaultPrimaryChargeId: string | null;
      primaryChargeIdToPriceMap: { id: string; price: number }[];
      primaryChargeType?: PrimaryChargeType | null;
      onSaveCallback: (row: EventSignUpRow) => void;
      stepToStartOn?: EventSignUpProcessSteps;
      isStaff?: boolean;
      isStaffAdmin?: boolean;
      eventRegistrationCleanup?: boolean;
      memberSignUpProxyAllowed?: boolean;
      eventHasWaitlist?: boolean;
      isWaitlistSignup?: boolean;
      eventRemainingCapacity: {
        capacityType: EventCapacityType;
        remainingCapacity: number;
      } | null;
    }
  ) => {
    set(_currentMemberUserIdAtom, payload.currentMemberUserId);
    set(resolvedPrimaryChargeSelectionQueryKeysAtom, []);
    set(_isStaffAdminAtom, payload.isStaffAdmin ?? false);
    set(requiredEventSignUpStepsAtom, payload.steps);
    set(_originalSignUpRowAtom, payload.signUpRow);
    set(_currentSignUpRowAtom, payload.signUpRow);
    set(_primaryChargeTypeAtom, payload.primaryChargeType ?? null);
    set(
      currentEventSignUpStepAtom,
      payload.stepToStartOn ? payload.stepToStartOn : payload.steps[0]
    );
    set(defaultPrimaryChargeRuleIdAtom, payload.defaultPrimaryChargeId);
    set(primaryChargeRuleIdtoPriceMapAtom, payload.primaryChargeIdToPriceMap);
    set(_eventDetailFormConfigurationAtom, payload.formQuestionConfigurations);
    set(_groupSizeRequirementsAtom, payload.groupSizeRequirements);
    set(_isNewEventSignUpAtom, payload.signUpRow.id === null);
    set(isStaffAtom, !!payload.isStaff);
    set(onSaveCallbackAtom, () => {
      return payload.onSaveCallback;
    });
    set(hydrateUnknownGuestPlayerCountsAtom, { signUpRow: payload.signUpRow });
    set(_isMemberSignUpProxyAllowedAtom, !!payload.memberSignUpProxyAllowed);
    set(_eventHasWaitlistAtom, !!payload.eventHasWaitlist);
    set(_isWaitlistSignupAtom, !!payload.isWaitlistSignup);
    set(
      eventRemainingCapacityAtom,
      payload.eventRemainingCapacity
        ? {
            capacityType: payload.eventRemainingCapacity.capacityType,
            remainingCapacity: payload.eventRemainingCapacity.remainingCapacity,
          }
        : null
    );
  }
);

/**
 * Handles updating a single registrant's primary charge selection
 */

export const updatePrimaryChargeSelectionForSingleRegistrant = atom(
  null,
  (get, set, payload: { index: number; primaryChargeTypeId: string }) => {
    const currentSignUpRow = get(currentSignUpRowAtom);
    if (currentSignUpRow) {
      const newSignUpRow = {
        ...currentSignUpRow,
        registrations: currentSignUpRow.registrations.map((r) => {
          if (r.index === payload.index) {
            return {
              ...r,
              primaryRegistrationChargeTypeId: payload.primaryChargeTypeId,
            };
          } else {
            return r;
          }
        }),
      };
      set(_currentSignUpRowAtom, newSignUpRow);
    }
  }
);

/**
 * Updates ALL regisrants to a given primary charge type id.
 */
export const updatePrimaryChargeSelectionForWholeGroup = atom(
  null,
  (get, set, payload: { primaryChargeTypeId: string }) => {
    const currentSignUpRow = get(currentSignUpRowAtom);
    if (currentSignUpRow) {
      const newSignUpRow = {
        ...currentSignUpRow,
        registrations: currentSignUpRow.registrations.map((r) => {
          return {
            ...r,
            primaryRegistrationChargeTypeId: payload.primaryChargeTypeId,
          };
        }),
      };
      set(_currentSignUpRowAtom, newSignUpRow);
    }
  }
);

/**
 * General dispatch. Should slowly be refactored to isolate atoms
 */

/**
 * Action dispatch
 */

type EventSignUpProcessActions =
  | { type: "clear" }
  | {
      type: "addPlayer";
      payload: {
        player: EventPlayer;
        type: "GUEST" | "MEMBER" | "New Guest" | "CUSTOMER";
      };
    }
  | {
      type: "removeMemberHold";
    }
  | {
      type: "removeUnknownGuest";
    }
  | {
      type: "removeUnknownCustomer";
    }
  | {
      type: "removeRegistration";
      payload: { registration: EventSignUpRegistration };
    }
  | { type: "nextStep" }
  | { type: "previousStep" }
  | {
      type: "updateFormQuestionResponse";
      payload: {
        id: string;
        value?: string;
        booleanValue?: boolean;
        transportationTypeId?: string;
        registration: EventSignUpRegistration;
      };
    }
  | {
      type: "quickUpdatePrimaryChargePaymentOption";
      payload: {
        paymentOption: PrimaryChargeQuickPayOption;
      };
    }
  | {
      type: "updatePrimaryChargePayForWhoChoices";
      payload: {
        payerId: string;
        payedForIds: string[];
      };
    };

export const eventSignUpProcessDispatchAtom = atom(
  null,
  (get, set, action: EventSignUpProcessActions) => {
    const isCurrentStepValid = get(isCurrentStepValidAtom);
    const currentSignUpRow = get(_currentSignUpRowAtom);
    const currentMemberUserId = get(_currentMemberUserIdAtom);
    const currentStepIndex = get(currentStepIndexAtom);
    const requiredSteps = get(requiredEventSignUpStepsAtom);
    const eventFormQuestionConfigurations = get(
      eventDetailFormConfigurationAtom
    );
    const isCurrentMemberOwner = get(isCurrentMemberOwnerAtom);

    const isCurrentMemberRegistered = get(isCurrentMemberRegisteredAtom);

    const defaultPayingMemberId =
      isCurrentMemberOwner && !isCurrentMemberRegistered
        ? currentMemberUserId
        : currentMemberUserId ??
          currentSignUpRow?.registrations.find(
            (registration) => registration.type === "MEMBER"
          )?.registrant?.playerId ??
          currentSignUpRow?.registrations.find(
            (registration) =>
              registration.type === "CUSTOMER" &&
              !!registration.registrant.playerId
          )?.registrant?.playerId ??
          null;

    switch (action.type) {
      case "clear":
        set(requiredEventSignUpStepsAtom, []);
        set(_currentSignUpRowAtom, null);
        set(_currentMemberUserIdAtom, null);
        set(currentEventSignUpStepAtom, null);
        set(_eventDetailFormConfigurationAtom, []);
        set(_primaryChargeQuickPayOptionAtom, "you_pay_for_everyone");
        set(unknownGuestsAssignedToPayersAtom, []);
        set(_groupSizeRequirementsAtom, null);
        set(_isNewEventSignUpAtom, true);
        set(isStaffAtom, false);
        set(_isWaitlistSignupAtom, false);
        set(onSaveCallbackAtom, null);
        set(defaultPrimaryChargeRuleIdAtom, null);
        set(_primaryChargeTypeAtom, null);
        set(primaryChargeRuleIdtoPriceMapAtom, []);
        set(resolvedPrimaryChargeSelectionQueryKeysAtom, []);
        break;
      case "addPlayer":
        if (currentSignUpRow) {
          const ownerClient = currentSignUpRow.ownerClient
            ? currentSignUpRow.ownerClient
            : action.payload.player.playerId
            ? {
                id: action.payload.player.playerId,
                firstName: action.payload.player.firstName ?? "",
                lastName: action.payload.player.lastName ?? "",
              }
            : undefined;
          const newSignUpRow: EventSignUpRow = {
            ...currentSignUpRow,
            ownerClient,
            registrations: [
              ...currentSignUpRow.registrations.map((registration) => {
                /**
                 * If user adds a guest or unknown customer first - they will have no default
                 * paying member ID on staff side.
                 *
                 * So we fix those on subsequent adds.
                 */
                if (registration.payingMemberId === "") {
                  return {
                    ...registration,
                    payingMemberId: defaultPayingMemberId
                      ? defaultPayingMemberId
                      : action.payload.player.playerId ?? "",
                  };
                }
                return registration;
              }),
              {
                id: null,
                index: currentSignUpRow.registrations.length,
                type: action.payload.type,
                registrant: action.payload.player,
                formResponses: [],
                payingMemberId: defaultPayingMemberId
                  ? defaultPayingMemberId
                  : action.payload.type === "MEMBER" ||
                    action.payload.type === "CUSTOMER"
                  ? action.payload.player.playerId ?? ""
                  : "",
                // Charge defaults are resolved per registration after the
                // registrant (or guest payer) determines its sign-up window.
                primaryRegistrationChargeTypeId: null,
              },
            ],
          };

          set(_currentSignUpRowAtom, newSignUpRow);
        }
        break;
      case "removeMemberHold":
        if (currentSignUpRow) {
          const memberHoldIndex = currentSignUpRow.registrations.findIndex(
            (r) => r.type === "MEMBER" && !r.registrant.playerId
          );
          const removedMemberHoldRegistrations = [
            ...currentSignUpRow.registrations.slice(0, memberHoldIndex),
            ...currentSignUpRow.registrations.slice(memberHoldIndex + 1),
          ];
          const newSignUpRow = {
            ...currentSignUpRow,
            registrations: removedMemberHoldRegistrations.map((r, i) => ({
              ...r,
              index: i,
            })),
          };

          set(_currentSignUpRowAtom, newSignUpRow);
        }
        break;
      case "removeUnknownGuest":
        if (currentSignUpRow) {
          const unknownGuestIndex = currentSignUpRow.registrations.findIndex(
            (r) => r.type === "GUEST" && !r.registrant.playerId
          );
          const removedUnknownGuestRegistrations = [
            ...currentSignUpRow.registrations.slice(0, unknownGuestIndex),
            ...currentSignUpRow.registrations.slice(unknownGuestIndex + 1),
          ];
          const newSignUpRow = {
            ...currentSignUpRow,
            registrations: removedUnknownGuestRegistrations.map((r, i) => ({
              ...r,
              index: i,
            })),
          };
          set(_currentSignUpRowAtom, newSignUpRow);
        }
        break;
      case "removeUnknownCustomer":
        if (currentSignUpRow) {
          const unknownCustomerIndex = currentSignUpRow.registrations.findIndex(
            (r) => r.type === "CUSTOMER" && !r.registrant.playerId
          );
          if (unknownCustomerIndex >= 0) {
            const removedUnknownCustomerRegistrations = [
              ...currentSignUpRow.registrations.slice(0, unknownCustomerIndex),
              ...currentSignUpRow.registrations.slice(unknownCustomerIndex + 1),
            ];
            const newSignUpRow = {
              ...currentSignUpRow,
              registrations: removedUnknownCustomerRegistrations.map(
                (r, i) => ({
                  ...r,
                  index: i,
                })
              ),
            };
            set(_currentSignUpRowAtom, newSignUpRow);
          }
        }
        break;
      case "removeRegistration":
        const memberSignUpProxyAllowed = get(_isMemberSignUpProxyAllowedAtom);

        if (currentSignUpRow && action.payload.registration.index !== null) {
          const indexToDelete = action.payload.registration.index;
          /**
           * We might remove a paying member - if so - we need a new
           * default paying member id to update rows with.
           */
          const registrationToRemove = currentSignUpRow.registrations.find(
            (registration) =>
              registration.registrant?.playerId ===
              action.payload.registration.registrant.playerId
          );

          const needsNewDefaultMemberId =
            registrationToRemove?.type === "MEMBER" &&
            registrationToRemove?.registrant?.playerId ===
              defaultPayingMemberId &&
            !memberSignUpProxyAllowed;

          const newDefaultMemberId = needsNewDefaultMemberId
            ? currentSignUpRow.registrations.find(
                (registration) =>
                  registration.type === "MEMBER" &&
                  defaultPayingMemberId !== registration.registrant.playerId
              )?.registrant.playerId ?? ""
            : defaultPayingMemberId ?? "";

          const newSignUpRow = {
            ...currentSignUpRow,
            registrations: [
              ...currentSignUpRow.registrations.flatMap(
                (registration, index) => {
                  //Remove the targeted index
                  if (index === action.payload.registration.index) {
                    return [];
                  }

                  //Since we are removing an item - we need to correct our indices
                  const curRegistrationIndex = registration.index ?? 0;
                  const newRegistrationIndex =
                    curRegistrationIndex > indexToDelete
                      ? curRegistrationIndex - 1
                      : curRegistrationIndex;

                  //For others - check if we were being payed for by the player being removed
                  //if so, update our paying member ID to be the default
                  return [
                    {
                      ...registration,
                      index: newRegistrationIndex,
                      payingMemberId:
                        registration.payingMemberId ===
                        registrationToRemove?.registrant.playerId
                          ? newDefaultMemberId
                          : registration.payingMemberId,
                    },
                  ];
                }
              ),
            ],
          };
          set(_currentSignUpRowAtom, newSignUpRow);
        }
        break;
      case "nextStep":
        if (
          isCurrentStepValid &&
          currentStepIndex !== null &&
          currentStepIndex < requiredSteps.length - 1
        ) {
          set(currentEventSignUpStepAtom, requiredSteps[currentStepIndex + 1]);
        }

        break;
      case "previousStep":
        if (currentStepIndex !== null && currentStepIndex > 0) {
          set(currentEventSignUpStepAtom, requiredSteps[currentStepIndex - 1]);
        }

        break;

      case "updateFormQuestionResponse":
        const { value, booleanValue, transportationTypeId, id, registration } =
          action.payload;

        if (currentSignUpRow) {
          const newSignUpRow = { ...currentSignUpRow };

          const curRegistration = newSignUpRow?.registrations?.find((r) => {
            // For known players, match by playerId
            if (registration.registrant.playerId && r.registrant.playerId) {
              return r.registrant.playerId === registration.registrant.playerId;
            }
            // For unknown players, match by index (which should be unique)
            return r.index === registration.index;
          });

          const formState =
            eventFormQuestionConfigurations?.map((f) => {
              const formResponse = curRegistration?.formResponses?.find(
                (fr) => fr.questionConfigurationId === f.id
              );

              return {
                id: f.id ?? "",
                value: formResponse?.stringValue ?? undefined,
                booleanValue: formResponse?.booleanValue ?? undefined,
                transportationTypeId:
                  formResponse?.transportationTypeId ?? undefined,
              };
            }) || [];

          const formResponses: EventFormResponseInput[] = formState.map((f) => {
            if (f.id === id) {
              return {
                stringValue: value,
                booleanValue,
                questionConfigurationId: id,
                transportationTypeId,
              };
            } else {
              return {
                stringValue: f.value,
                booleanValue: f.booleanValue,
                questionConfigurationId: f.id,
                transportationTypeId: f.transportationTypeId,
              };
            }
          });

          newSignUpRow.registrations = newSignUpRow.registrations?.map(
            (r, i) => {
              // Use the same matching logic as above
              const isMatch = (() => {
                // For known players, match by playerId
                if (registration.registrant.playerId && r.registrant.playerId) {
                  return (
                    r.registrant.playerId === registration.registrant.playerId
                  );
                }
                // For unknown players, match by index (which should be unique)
                return r.index === registration.index;
              })();

              if (isMatch) {
                return {
                  ...r,
                  formResponses,
                };
              } else {
                return r;
              }
            }
          );

          set(_currentSignUpRowAtom, newSignUpRow);
        }
        break;
      case "quickUpdatePrimaryChargePaymentOption":
        set(_primaryChargeQuickPayOptionAtom, action.payload.paymentOption);
        if (defaultPayingMemberId && currentSignUpRow) {
          const paymentOption = action.payload.paymentOption;
          //If you pay for everyone, we just update all the paying member Ids to be you.
          if (paymentOption === "you_pay_for_everyone") {
            const currentPaymentMap: UnknownGuestPrimaryChargePaymentMap[] = [];
            const unknownGuestRegistrations =
              currentSignUpRow.registrations.filter(
                (r) => !r.registrant.playerId && r.type === "GUEST"
              );
            unknownGuestRegistrations.forEach((ugr) => {
              const chargeId = ugr.primaryRegistrationChargeTypeId;
              const matchingEntry = currentPaymentMap.find(
                (pm) =>
                  pm.payerId === defaultPayingMemberId &&
                  pm.primaryChargeId === chargeId
              );
              if (matchingEntry) {
                matchingEntry.unknownGuestCount++;
              } else {
                currentPaymentMap.push({
                  primaryChargeId: chargeId,
                  payerId: defaultPayingMemberId,
                  unknownGuestCount: 1,
                });
              }
            });
            set(unknownGuestsAssignedToPayersAtom, currentPaymentMap);
            set(_currentSignUpRowAtom, {
              ...currentSignUpRow,
              registrations: currentSignUpRow?.registrations.map((r) => {
                return {
                  ...r,
                  payingMemberId: defaultPayingMemberId,
                };
              }),
            });
          }
          // //IF everyone pays for themselves, we update all MEMBERS to pay for themselves.
          // //We don't touch guests as they cannot pay for themselves.
          else if (paymentOption === "everyone_pays_for_themselves") {
            set(_currentSignUpRowAtom, {
              ...currentSignUpRow,
              registrations: currentSignUpRow?.registrations.map((r) => {
                const isMember = r.type === "MEMBER";
                const curRegistrantMemberId = isMember
                  ? r.registrant.playerId
                  : null;
                return isMember && curRegistrantMemberId
                  ? {
                      ...r,
                      payingMemberId: curRegistrantMemberId,
                    }
                  : r;
              }),
            });
          }
        }
        break;
      case "updatePrimaryChargePayForWhoChoices":
        if (currentSignUpRow) {
          const updateRegistrations =
            currentSignUpRow?.registrations.flatMap((r) => {
              //IF we passed up the player ID as someone we are paying for
              //update the payingMemberId, and zero out any unknown guests they
              //were paying for
              if (
                r.registrant.playerId &&
                action.payload.payedForIds.includes(r.registrant.playerId)
              ) {
                if (r.registrant.playerId && r.type === "MEMBER") {
                  set(zeroOutUnknownGuestPaymentsForPayer, {
                    payerId: r.registrant.playerId,
                  });
                }

                return [
                  {
                    ...r,
                    payingMemberId: action.payload.payerId,
                  },
                ];
              } else if (
                r.type === "MEMBER" &&
                r.registrant.playerId &&
                r.payingMemberId === action.payload.payerId
              ) {
                /**
                 * If we are a member, that WAS being payed for by the passed ID,
                 * and we are no longer being payed for - then we are paying for ourself.
                 */
                return {
                  ...r,
                  payingMemberId: r.registrant.playerId,
                };
              } else if (
                r.type !== "MEMBER" &&
                r.payingMemberId === action.payload.payerId
              ) {
                /**
                 * If we are not a member, and no longer being payed for... then I don't know how to handle it
                 * //NOTE: You cannot uncheck guests so this will never happen.
                 */
                return {
                  ...r,
                };
              } else {
                return r;
              }
            }) ?? [];
          set(_currentSignUpRowAtom, {
            ...currentSignUpRow,
            registrations: updateRegistrations,
          });
        }
        break;
      default:
        break;
    }
  }
);

export const notifyMembersAtom = atom(false);
